<template>
  <modal
    name="DocumentOrderAdminModal"
    :width="'90%'"
    :max-width="680"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    class="document-order-vmodal"
    @closed="resetState"
  >
    <section class="document-order-modal" aria-labelledby="document-order-title">
      <header class="document-order-header">
        <div>
          <h2 id="document-order-title">Definir orden de documentos</h2>
          <p>Seleccione un grupo y ordene solamente los documentos asignados a él.</p>
        </div>
        <button type="button" class="close-button" title="Cerrar" :disabled="saving" @click="close">&times;</button>
      </header>

      <div class="document-order-body">
        <label class="group-field">
          Grupo de usuarios
          <select v-model="selectedGroup" :disabled="loadingGroups || saving" @change="loadDocuments">
            <option value="" disabled>Seleccione un grupo</option>
            <option v-for="group in orderGroups" :key="group.id" :value="group.id">
              {{ group.name }}
            </option>
          </select>
        </label>

        <div v-if="loadingGroups || loadingDocuments" class="document-order-state">
          <font-awesome-icon icon="spinner" spin />
          <span>Cargando documentos...</span>
        </div>
        <div v-else-if="errorMessage && orderedDocuments.length === 0" class="document-order-error" role="alert">
          {{ errorMessage }}
        </div>
        <div v-else-if="orderGroups.length === 0" class="document-order-state">
          No hay grupos disponibles.
        </div>
        <div v-else-if="selectedGroup && orderedDocuments.length === 0" class="document-order-state">
          Este grupo no tiene documentos para ordenar.
        </div>

        <ol v-else-if="orderedDocuments.length" class="document-order-list">
          <li
            v-for="(document, index) in orderedDocuments"
            :key="document.id"
            :class="{ dragging: draggedIndex === index }"
            draggable="true"
            @dragstart="startDrag(index, $event)"
            @dragover.prevent
            @drop="dropDocument(index)"
            @dragend="draggedIndex = null"
          >
            <span class="document-position">{{ index + 1 }}</span>
            <span class="drag-handle" title="Arrastrar para reordenar"><font-awesome-icon icon="sort" /></span>
            <span class="document-name">{{ document.filename }}</span>
            <div class="document-order-controls">
              <button type="button" title="Subir documento" :disabled="index === 0 || saving" @click="moveDocument(index, -1)">
                <font-awesome-icon icon="arrow-up" />
              </button>
              <button type="button" title="Bajar documento" :disabled="index === orderedDocuments.length - 1 || saving" @click="moveDocument(index, 1)">
                <font-awesome-icon icon="arrow-down" />
              </button>
            </div>
          </li>
        </ol>

        <div v-if="errorMessage && orderedDocuments.length" class="document-order-error" role="alert">{{ errorMessage }}</div>
      </div>

      <footer class="document-order-footer">
        <button type="button" class="button-secondary" :disabled="saving" @click="close">Cancelar</button>
        <button
          type="button"
          class="button-primary"
          :disabled="loadingGroups || loadingDocuments || saving || !selectedGroup || !orderedDocuments.length || !hasChanges"
          @click="saveOrder"
        >
          <font-awesome-icon v-if="saving" icon="spinner" spin />
          {{ saving ? 'Guardando...' : 'Guardar orden' }}
        </button>
      </footer>
    </section>
  </modal>
</template>

<script>
import { restDocument } from '@/components/mixin/restDocument.js'

export default {
  name: 'DocumentOrderAdminModal',
  mixins: [restDocument],
  data() {
    return {
      orderGroups: [],
      selectedGroup: '',
      orderedDocuments: [],
      originalOrder: [],
      loadingGroups: false,
      loadingDocuments: false,
      saving: false,
      errorMessage: '',
      draggedIndex: null,
      requestSequence: 0
    }
  },
  computed: {
    hasChanges() {
      const orderChanged = this.orderedDocuments.some((document, index) => document.id !== this.originalOrder[index])
      const needsNormalization = this.orderedDocuments.some((document, index) => {
        const groupOrder = (document.group_orders || []).find(item => item.id_group === this.selectedGroup)
        return !groupOrder || Number(groupOrder.order) !== index + 1
      })
      return orderChanged || needsNormalization
    }
  },
  methods: {
    show() {
      this.resetState()
      this.loadingGroups = true
      this.$modal.show('DocumentOrderAdminModal')
      this.getDocumentGroups()
        .then(() => {
          this.orderGroups = (this.groups || []).slice().sort((left, right) =>
            (left.name || '').localeCompare(right.name || '', 'es', { sensitivity: 'base' })
          )
          if (this.orderGroups.length) {
            this.selectedGroup = this.orderGroups[0].id
            this.loadDocuments()
          }
        })
        .catch(error => {
          this.errorMessage = this.getErrorMessage(error, 'No fue posible cargar los grupos.')
        })
        .then(() => {
          this.loadingGroups = false
        })
    },
    loadDocuments() {
      const requestID = ++this.requestSequence
      this.orderedDocuments = []
      this.originalOrder = []
      this.errorMessage = ''
      if (!this.selectedGroup) return
      this.loadingDocuments = true
      this.getDocumentsForOrder(this.selectedGroup)
        .then(documents => {
          if (requestID !== this.requestSequence) return
          this.orderedDocuments = documents.map(document => ({ ...document }))
          this.originalOrder = documents.map(document => document.id)
        })
        .catch(error => {
          if (requestID !== this.requestSequence) return
          this.errorMessage = this.getErrorMessage(error, 'No fue posible cargar los documentos del grupo.')
        })
        .then(() => {
          if (requestID === this.requestSequence) this.loadingDocuments = false
        })
    },
    close() {
      if (!this.saving) this.$modal.hide('DocumentOrderAdminModal')
    },
    resetState() {
      this.requestSequence++
      this.orderGroups = []
      this.groups = []
      this.selectedGroup = ''
      this.orderedDocuments = []
      this.originalOrder = []
      this.loadingGroups = false
      this.loadingDocuments = false
      this.saving = false
      this.errorMessage = ''
      this.draggedIndex = null
    },
    startDrag(index, event) {
      if (this.saving) return
      this.draggedIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(index))
    },
    dropDocument(targetIndex) {
      if (this.draggedIndex === null || this.draggedIndex === targetIndex || this.saving) return
      const movedDocument = this.orderedDocuments.splice(this.draggedIndex, 1)[0]
      this.orderedDocuments.splice(targetIndex, 0, movedDocument)
      this.draggedIndex = null
    },
    moveDocument(index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= this.orderedDocuments.length) return
      const movedDocument = this.orderedDocuments.splice(index, 1)[0]
      this.orderedDocuments.splice(targetIndex, 0, movedDocument)
    },
    saveOrder() {
      if (!this.hasChanges || this.saving) return
      this.saving = true
      this.errorMessage = ''
      const documentIDs = this.orderedDocuments.map(document => document.id)
      this.putDocumentsOrder(this.selectedGroup, documentIDs)
        .then(() => {
          this.$emit('saved')
          this.$modal.hide('DocumentOrderAdminModal')
        })
        .catch(error => {
          this.errorMessage = this.getErrorMessage(error, 'No fue posible guardar el orden de los documentos.')
        })
        .then(() => {
          this.saving = false
        })
    },
    getErrorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback
    }
  }
}
</script>

<style lang="scss" scoped>
.document-order-vmodal::v-deep .vm--modal { background: transparent; box-shadow: none; }
.document-order-modal { overflow: hidden; width: 100%; max-height: 88vh; border-radius: 8px; background: #fff; box-shadow: 0 18px 48px rgba(15,42,61,.28); color: var(--color-navy); }
.document-order-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; padding: 1.25rem 1.4rem; border-bottom: 1px solid #dfe6eb; }
.document-order-header h2 { margin: 0 0 .35rem; font-size: 1.15rem; }
.document-order-header p { margin: 0; color: #667786; font-size: .82rem; line-height: 1.45; }
.close-button { flex: 0 0 auto; width: 30px; height: 30px; border: 0; border-radius: 4px; background: transparent; color: #667786; font-size: 1.4rem; cursor: pointer; }
.close-button:hover:not(:disabled) { background: #edf2f5; color: var(--color-navy); }
.document-order-body { overflow-y: auto; max-height: 60vh; padding: 1rem 1.4rem; background: #f7f9fa; }
.group-field { display: flex; flex-direction: column; gap: .35rem; margin-bottom: .9rem; color: #32485a; font-size: .82rem; font-weight: 600; }
.group-field select { width: 100%; padding: .45rem .55rem; border: 1px solid #cbd6de; border-radius: 4px; background: #fff; }
.document-order-state { display: flex; align-items: center; justify-content: center; gap: .6rem; min-height: 120px; color: #667786; }
.document-order-list { display: flex; flex-direction: column; gap: .45rem; margin: 0; padding: 0; list-style: none; }
.document-order-list li { display: grid; grid-template-columns: 32px 24px minmax(0,1fr) auto; align-items: center; gap: .55rem; min-height: 44px; padding: .45rem .55rem; border: 1px solid #d7e0e6; border-radius: 5px; background: #fff; cursor: grab; }
.document-order-list li.dragging { opacity: .5; border-color: var(--color-orange); }
.document-position { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 4px; background: #edf2f5; font-size: .78rem; font-weight: 700; }
.drag-handle { color: #8a9aa7; text-align: center; }
.document-name { overflow: hidden; color: #32485a; font-size: .86rem; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.document-order-controls { display: flex; gap: .35rem; }
.document-order-controls button { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: 1px solid #cbd6de; border-radius: 4px; background: #fff; color: var(--color-navy); cursor: pointer; }
.document-order-controls button:hover:not(:disabled) { border-color: var(--color-orange); color: var(--color-orange); }
.document-order-controls button:disabled,.close-button:disabled { opacity: .4; cursor: not-allowed; }
.document-order-error { margin-top: .8rem; padding: .65rem .8rem; border: 1px solid #edb9b5; border-radius: 4px; background: #fff1f0; color: #922b21; font-size: .82rem; }
.document-order-footer { display: flex; justify-content: flex-end; gap: .6rem; padding: 1rem 1.4rem; border-top: 1px solid #dfe6eb; background: #fff; }
.document-order-footer button { display: inline-flex; align-items: center; justify-content: center; gap: .45rem; min-height: 36px; padding: .45rem .9rem; border-radius: 4px; font-size: .82rem; font-weight: 600; cursor: pointer; }
.button-secondary { border: 1px solid #cbd6de; background: #fff; color: var(--color-navy); }
.button-primary { border: 1px solid var(--color-navy); background: var(--color-navy); color: #fff; }
.document-order-footer button:disabled { opacity: .5; cursor: not-allowed; }
@media (max-width: 520px) { .document-order-list li { grid-template-columns: 28px 20px minmax(0,1fr); } .document-order-controls { grid-column: 1 / -1; justify-content: flex-end; } }
</style>
